import { Dropdown } from "primereact/dropdown"
import { Panel } from "primereact/panel"
import { useEffect, useMemo, useState } from "react"
import { Product_Output, ProductionTask_Input } from "@/api"
import { formatFloat } from "@/utils/format"

interface MachineSelectorProps {
  selectedMachine?: string | null
  onMachineChange?: (machineId: string | null) => void
  productionTasks?: ProductionTask_Input[]
  productsById?: Record<string, Product_Output>
  dates?: string[]
  holidayDates?: string[]
}

const MachineSelector = ({
  selectedMachine,
  onMachineChange,
  productionTasks = [],
  productsById = {},
  dates = [],
  holidayDates = [],
}: MachineSelectorProps) => {
  const [internalSelected, setInternalSelected] = useState<string | null>(selectedMachine || null)

  useEffect(() => {
    setInternalSelected(selectedMachine || null)
  }, [selectedMachine])

  const machineStats = useMemo(() => {
    const stats = new Map<string, { duration: number; taskCount: number }>()
    Object.values(productsById).forEach((product) => {
      Object.keys(product.machine_variants ?? {}).forEach((machineId) => {
        if (!stats.has(machineId)) {
          stats.set(machineId, { duration: 0, taskCount: 0 })
        }
      })
    })
    productionTasks.forEach((task) => {
      const prev = stats.get(task.machine_id) ?? { duration: 0, taskCount: 0 }
      prev.duration += task.duration ?? 0
      prev.taskCount += 1
      stats.set(task.machine_id, prev)
    })
    return stats
  }, [productionTasks, productsById])

  const workingDayCount = useMemo(() => {
    if (!dates.length) return 0
    const holidaySet = new Set(holidayDates)
    return dates.filter((d) => !holidaySet.has(d)).length
  }, [dates, holidayDates])

  const dayBaseSeconds = 24 * 60 * 60

  const machineOptions = useMemo(() => {
    const ids = Array.from(machineStats.keys()).sort((a, b) => a.localeCompare(b))
    return ids.map((machineId) => {
      const stat = machineStats.get(machineId) ?? { duration: 0, taskCount: 0 }
      const totalHours = stat.duration / 3600
      const capacitySeconds = workingDayCount * dayBaseSeconds
      const utilization = capacitySeconds > 0 ? stat.duration / capacitySeconds : 0
      const label = `${machineId} - ${formatFloat(totalHours, 1)}h (${formatFloat(utilization * 100, 1)}%)`
      let className = "date-heat-option"
      if (utilization >= 0.8) {
        className += " high"
      } else if (utilization >= 0.4) {
        className += " medium"
      } else if (utilization > 0) {
        className += " low"
      }
      return { label, value: machineId, className }
    })
  }, [dayBaseSeconds, machineStats, workingDayCount])

  const handleChange = (value: string | null) => {
    setInternalSelected(value)
    if (onMachineChange) {
      onMachineChange(value)
    }
  }

  return (
    <Panel toggleable header="成形機毎のサマリ" className="w-full">
      <div className="p-3">
        <div className="mb-2">
          <label className="font-semibold text-sm">成形機選択</label>
        </div>
        <Dropdown
          value={internalSelected}
          onChange={(e) => handleChange(e.value)}
          options={machineOptions}
          placeholder="成形機を選択してください"
          className="w-full"
          showClear
          filter
        />
        {machineOptions.length > 0 && (
          <div className="mt-2 text-xs text-600">
            登録成形機数: {machineOptions.length} / 稼働日数: {workingDayCount}
          </div>
        )}
      </div>
    </Panel>
  )
}

export default MachineSelector
